import React, { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { TEMPLATES } from '../lib/data';
import { OUTCOME_EDUCATION } from '../lib/outcomeEducation';
import { PageHeader } from './PageHeader';
import { FilterSidebar } from './FilterSidebar';
import { FunnelTypeGuide } from './FunnelTypeGuide';
import { TemplateGrid } from './TemplateGrid';
import { QuizRecommender } from './QuizRecommender';
export function FunnelTemplateGallery() {
  // Filtering state
  const [selectedFunnelTypes, setSelectedFunnelTypes] = useState<string[]>([]);
  const [selectedOutcome, setSelectedOutcome] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const toggleFunnelType = (id: string) => {
    setSelectedFunnelTypes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const selectOutcome = (outcome: string) => {
    setSelectedOutcome(prev => prev === outcome ? null : outcome);
  };
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  // Get the funnel type IDs that match the selected outcome
  const outcomeTypeIds = useMemo(() => {
    if (!selectedOutcome) return null;
    const education = OUTCOME_EDUCATION.find(e => e.outcome === selectedOutcome);
    return education ? education.recommendedFunnelTypes : null;
  }, [selectedOutcome]);
  // Filter templates
  const filteredTemplates = useMemo(() => {
    return TEMPLATES.filter(template => {
      // Outcome filter — uses recommendedFunnelTypes from education data
      const matchesOutcome = outcomeTypeIds ? outcomeTypeIds.includes(template.typeId) : true;
      const matchesFunnelType = selectedFunnelTypes.length > 0 ? selectedFunnelTypes.includes(template.typeId) : true;
      const matchesCategory = selectedCategories.length > 0 ? selectedCategories.includes(template.category) : true;
      const matchesSearch = searchQuery ? template.title.toLowerCase().includes(searchQuery.toLowerCase()) || template.description.toLowerCase().includes(searchQuery.toLowerCase()) : true;
      return matchesOutcome && matchesFunnelType && matchesCategory && matchesSearch;
    });
  }, [selectedFunnelTypes, selectedCategories, searchQuery, outcomeTypeIds]);
  const hasActiveFilters = selectedFunnelTypes.length > 0 || selectedOutcome !== null || selectedCategories.length > 0;
  return <div className="min-h-screen bg-background text-foreground font-sans" data-id="element-34">
      <div className="max-w-[1440px] mx-auto p-4 md:p-6 lg:p-8" data-id="element-35">
        <PageHeader data-id="element-36" />

        <div className="flex flex-col md:flex-row gap-6" data-id="element-37">
          {/* Left Sidebar — handles ALL filtering */}
          <FilterSidebar selectedOutcome={selectedOutcome} onSelectOutcome={selectOutcome} selectedFunnelTypes={selectedFunnelTypes} onToggleFunnelType={toggleFunnelType} selectedCategories={selectedCategories} onToggleCategory={toggleCategory} data-id="element-38" />

          {/* Main Content Area */}
          <div className="flex-1 min-w-0" data-id="element-39">
            {/* Search Bar */}
            <div className="relative mb-6" data-id="element-40">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-41">
                <SearchIcon className="h-5 w-5 text-muted-foreground" data-id="element-42" />
              </div>
              <input type="text" className="block w-full max-w-sm pl-11 pr-4 py-2.5 border border-input rounded-lg leading-5 bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring text-sm shadow-sm transition-shadow" placeholder="Search for a template..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-43" />
            </div>

            {/* Dynamic Education Area — driven by sidebar outcome selection */}
            <FunnelTypeGuide selectedOutcome={selectedOutcome} onOpenQuiz={() => setIsQuizOpen(true)} data-id="element-44" />

            {/* Results Header */}
            <div className="flex items-center justify-between mb-4" data-id="element-45">
              <h2 className="text-xl font-bold text-foreground" data-id="element-46">
                {hasActiveFilters ? 'Your Results' : 'All Templates'}
              </h2>
              <span className="text-sm text-muted-foreground font-medium" data-id="element-47">
                Showing {filteredTemplates.length} results
              </span>
            </div>

            {/* Template Grid */}
            <TemplateGrid templates={filteredTemplates} data-id="element-48" />
          </div>
        </div>
      </div>

      {/* Modals */}
      <QuizRecommender isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} onRecommend={typeId => {
      if (typeId === 'learn-all') {
        // Select the first outcome to trigger education
        setSelectedOutcome('Get more followers');
      } else {
        setSelectedFunnelTypes([typeId]);
      }
    }} data-id="element-49" />
    </div>;
}